<template>
  <div class="form-group">
    <h4 v-if="title" class="cat-title">{{ title }}</h4>
    <label v-else-if="label">{{ label }}</label>
    <div class="cat-btn-group">
      <button v-for="cat in categories" type="button"
        :class="['cat-btn', { active: isSelected(cat.value), full: isFull(cat.value), ineligible: isIneligible(cat.value) }]"
        :disabled="!isSelected(cat.value) && (isFull(cat.value) || isIneligible(cat.value))"
        :title="ineligibleReasons?.[cat.value]"
        @click="toggle(cat.value)" :key="cat.value">
        {{ cat.label }}<span v-if="isFull(cat.value)" class="full-badge"> Complet</span>
        <span v-else-if="ineligibleReasons?.[cat.value]" class="full-badge"> {{ ineligibleReasons[cat.value] }}</span>
      </button>
      <button type="button" :class="['cat-btn', { active: modelValue === null || modelValue === '' }]"
        @click="emit('update:modelValue', null)">
        Aucune
      </button>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
interface Category {
  value: string
  label: string
}

const props = defineProps<{
  modelValue: string | null
  categories: Category[]
  title?: string
  label?: string
  multiple?: boolean  // true = multi-sélection (individuel), false = exclusif (double)
  disabledValues?: string[]  // catégories complètes (quota atteint) -- grisées, label "Complet"
  // Catégories grisées pour une autre raison (non éligible selon l'âge/le genre du
  // joueur, ou romprait la règle de cumul) -- jamais "Complet", qui serait faux : la
  // catégorie n'est pas pleine, juste fermée à ce joueur précis.
  ineligibleValues?: string[]
  // Même chose avec la raison affichée sur le bouton ("Déjà inscrit", "Non éligible"...).
  // Une catégorie bloquée mais déjà cochée reste cliquable, pour pouvoir la décocher.
  ineligibleReasons?: Record<string, string>
  // Mode multi : la catégorie cliquée est-elle cumulable avec celles déjà cochées ? Si
  // non, elle les remplace (ex. Mixte cliqué alors que Vétéran est coché) plutôt que
  // d'obliger à passer par "Aucune". Absent : tout est cumulable.
  isCumulable?: (current: string[], candidate: string) => boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: string | null): void
}>()

const isSelected = (val: string): boolean => {
  if (!props.modelValue) return false
  if (props.multiple) {
    return props.modelValue.split(',').map(c => c.trim()).includes(val)
  }
  return props.modelValue === val
}

const isFull = (val: string): boolean => {
  return props.disabledValues?.includes(val) ?? false
}

const isIneligible = (val: string): boolean => {
  return (props.ineligibleValues?.includes(val) ?? false) || !!props.ineligibleReasons?.[val]
}

const toggle = (val: string) => {
  if (!props.multiple) {
    // Mode exclusif : toggle simple
    emit('update:modelValue', props.modelValue === val ? null : val)
    return
  }
  // Mode multi : cumul selon isCumulable, sinon remplacement
  const current = props.modelValue
    ? props.modelValue.split(',').map(c => c.trim()).filter(Boolean)
    : []

  if (current.includes(val)) {
    const next = current.filter(c => c !== val)
    emit('update:modelValue', next.length ? next.join(',') : null)
  } else if (props.isCumulable && !props.isCumulable(current, val)) {
    emit('update:modelValue', val)
  } else {
    const next = [...current, val].filter((v, i, a) => a.indexOf(v) === i)
    emit('update:modelValue', next.join(','))
  }
}
</script>

<style scoped>
.cat-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.cat-btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.3rem;
}

.cat-btn {
  padding: 0.4rem 1rem;
  border: 2px solid #cbd5e1;
  border-radius: 20px;
  background: #fff;
  color: #555;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;
}

.cat-btn:hover {
  border-color: var(--color-accent, #1a3a5c);
  color: var(--color-accent, #1a3a5c);
}

.cat-btn.active {
  background: var(--color-accent, #1a3a5c);
  border-color: var(--color-accent, #1a3a5c);
  color: #fff;
  font-weight: 600;
}

.cat-btn.full,
.cat-btn.ineligible,
.cat-btn:disabled {
  background: #e5e7eb;
  border-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  gap: 0.4rem;
}

/* Cochée alors qu'elle n'est pas permise (ex. joueur déjà inscrit choisi après coup) :
   à décocher, sinon l'inscription est refusée. */
.cat-btn.active.ineligible {
  background: #fee2e2;
  border-color: #dc2626;
  color: #b91c1c;
  cursor: pointer;
}

.full-badge {
  margin-left: 0.35rem;
  font-size: 0.72rem;
  font-style: italic;
  opacity: 0.8;
}
</style>
